import { useEffect, useState } from "react";
import { Alert, Spinner } from "react-bootstrap";
import { useHistory, useParams } from "react-router-dom";
import GeneralExpenseForm from "./_components/GeneralExpenseForm";
import {
  useCreateGeneralExpenseMutation,
  useGetGeneralExpenseById,
  useUpdateGeneralExpenseMutation,
} from "../../../../api/fleet-management/general-expenses/general-expenses.hooks";
import {
  buildGeneralExpensePayload,
  initialGeneralExpenseForm,
  mapGeneralExpenseToForm,
  validateGeneralExpenseForm,
} from "./utils/general-expense.utils";

const GeneralExpenseFormPage = () => {
  const history = useHistory();
  const { generalExpenseId } = useParams();
  const isEditMode = Boolean(generalExpenseId);

  const [form, setForm] = useState(initialGeneralExpenseForm);
  const [initialFormState, setInitialFormState] = useState(initialGeneralExpenseForm);
  const [validationErrors, setValidationErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");

  const {
    data: expenseData,
    isLoading: isExpenseLoading,
    isError: isExpenseError,
    error: expenseError,
  } = useGetGeneralExpenseById(generalExpenseId);

  const {
    mutate: createGeneralExpense,
    isLoading,
    isPending,
    error,
    reset,
  } = useCreateGeneralExpenseMutation();

  const {
    mutate: updateGeneralExpense,
    isLoading: isUpdating,
    isPending: isUpdatePending,
    error: updateError,
    reset: resetUpdate,
  } = useUpdateGeneralExpenseMutation();

  useEffect(() => {
    if (!isEditMode || !expenseData) return;
    const nextForm = mapGeneralExpenseToForm(expenseData);
    setForm(nextForm);
    setInitialFormState(nextForm);
  }, [expenseData, isEditMode]);

  const submitting = isLoading || isPending || isUpdating || isUpdatePending;
  const submitError = isEditMode ? updateError : error;

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setValidationErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = () => {
    const nextErrors = validateGeneralExpenseForm(form);
    setValidationErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSuccessMessage("");
    reset();
    resetUpdate();

    if (!validate()) return;

    const payload = buildGeneralExpensePayload(form);

    if (isEditMode) {
      updateGeneralExpense(
        { generalExpenseId, generalExpenseData: payload },
        {
          onSuccess: () => {
            setSuccessMessage("General expense updated successfully.");
            setInitialFormState(mapGeneralExpenseToForm(payload));
          },
        }
      );
      return;
    }

    createGeneralExpense(payload, {
      onSuccess: () => {
        setSuccessMessage("General expense created successfully.");
        setForm(initialGeneralExpenseForm);
      },
    });
  };

  if (isEditMode && isExpenseLoading) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isEditMode && isExpenseError) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <Alert variant="danger" className="mb-0">
              {expenseError?.response?.data?.message || "Failed to load general expense details."}
            </Alert>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-link p-0 text-decoration-none"
              onClick={() => history.push("/fleet-management/general-expenses")}
              aria-label="Back to general expenses list"
              title="Back"
            >
              <i className="bi bi-arrow-left fs-4" />
            </button>
            <h4 className="card-title mb-0">
              {isEditMode ? "Edit General Expense" : "Create General Expense"}
            </h4>
          </div>
          <span className="badge bg-info text-dark">Fleet Management</span>
        </div>

        <div className="card-body">
          <p className="text-muted mb-4">
            {isEditMode
              ? "Update general expense details."
              : "Fill in general expense details."}
          </p>

          {successMessage && (
            <Alert variant="success" className="mb-4">
              {successMessage}
            </Alert>
          )}

          {submitError && (
            <Alert variant="danger" className="mb-4">
              {submitError?.response?.data?.message ||
                `Failed to ${isEditMode ? "update" : "create"} general expense. Please try again.`}
            </Alert>
          )}

          <GeneralExpenseForm
            form={form}
            validationErrors={validationErrors}
            submitting={submitting}
            isEditMode={isEditMode}
            onSubmit={handleSubmit}
            onFieldChange={updateField}
            onReset={() => {
              setForm(isEditMode ? initialFormState : initialGeneralExpenseForm);
              setValidationErrors({});
              setSuccessMessage("");
              reset();
              resetUpdate();
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default GeneralExpenseFormPage;
